import {
  Button,
  Dropdown,
  Form,
  Menu,
  message,
  Modal,
  Pagination,
  Select,
  Table,
} from "antd";
import type { PaginationProps } from "antd";
import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect } from "react";
import CatalogHeader from "../../../components/_shared/CatalogHeader";
import { RootState } from "../../../redux/rootReducer";
import TrackModalForm from "../../../components/catalog/TrackModalForm";
import {
  deleteTrack,
  resetTracks,
  retrieveTracks,
} from "../../../redux/actions/trackActions";
import tableUtils from "../../../utils/tableUtils";
import { TrackColumn } from "../../../shared/table/columns";
import { retrieveLabels } from "../../../redux/actions/labelActions";
import { retrieveContributors } from "../../../redux/actions/contributorActions";
import { Actions, ContributorType, PageSize } from "../../../shared/constant";
import SearchComponent from "../../../components/_shared/SearchComponent";
import globalUtils from "../../../utils/globalUtils";
import icons from "../../../shared/icons";
import { isClapAdminORClapManager } from "../../../utils/clapUtils";

const blankOB = { id: "null", name: "BLANK" };

const SelectFields = ({ name, options }: { name: string; options: any }) => {
  const [isSelected, setIsSelected] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string[]>([]);
  const onChange = (value: string[]) => {
    if (value.length) {
      setIsSelected(true);
      if (value.toString() === blankOB.id) setSelectedValue([]);
      else setSelectedValue(value);
    } else {
      setIsSelected(false);
      setSelectedValue([]);
    }
  };

  return (
    <div>
      <Form.Item
        name={name === "Label" ? `${name.toLowerCase()}s` : name.toLowerCase()}
        label={name}
        className="mb-[6px]"
      >
        <Select
          mode="multiple"
          placeholder={`Select ${name}`}
          className="w-full"
          allowClear
          filterOption={(inputValue, option) =>
            option?.props.children
              .toString()
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          }
          onChange={onChange}
        >
          {options?.map((option: any, index: number) => (
            <Select.Option
              key={option.id}
              value={option.id}
              disabled={
                (isSelected && !selectedValue.length && index !== 0) ||
                (isSelected &&
                  selectedValue.length &&
                  !selectedValue.includes("") &&
                  index === 0)
              }
            >
              {name === "Track" ? option.title : option.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );
};

function Tracks() {
  const dispatch = useDispatch();
  const [refreshkey, setRefreshKey] = useState("");
  const [filterForm] = Form.useForm();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [mode, setMode] = useState("");
  const [editData, setEditData] = useState(null);
  const [effect, setEffect] = useState(false);
  const [visible, setVisible] = useState(false);
  // const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { tracks, tracksResponse, tablePending } = useSelector(
    (state: RootState) => state.track,
  );
  const { allLabels } = useSelector((state: RootState) => state.label);
  const { allArtists, allLyricists, allComposers } = useSelector(
    (state: RootState) => state.contributor,
  );
  const { pending, fulfilled } = useSelector((state: RootState) => state.track);
  const { userDetails } = useSelector((state: RootState) => state.user);

  const fetchLabelAndContributors = () => {
    dispatch(retrieveLabels({ page: "all" }));
    dispatch(
      retrieveContributors({
        type: ContributorType.ARTIST,
        page: "all",
      }),
    );
    dispatch(
      retrieveContributors({
        type: ContributorType.LYRICIST,
        page: "all",
      }),
    );
    dispatch(
      retrieveContributors({
        type: ContributorType.COMPOSER,
        page: "all",
      }),
    );
  };

  useEffect(() => {
    dispatch(resetTracks());
    fetchLabelAndContributors();
  }, []);

  useEffect(() => {
    dispatch(
      retrieveTracks({
        catalog: "catalog",
        page,
        searchText,
        ...filterForm.getFieldsValue(),
      }),
    );
  }, [page, effect]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  const showModal = () => {
    setMode(Actions.CREATE);
    setVisible(true);
  };

  const hideModal = () => {
    setVisible(false);
    setMode("");
    setEditData(null);
  };

  const callEffect = () => {
    setEffect(!effect);
  };

  /* const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    // console.log("selectedRowKeys changed: ", selectedRowKeys);
    // console.log(newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }; */

  useEffect(() => {
    if (mode === Actions.DELETE) {
      if (fulfilled) {
        message.success("Track successfully deleted");
        setMode("");
        if (tracks.length === 1 && page > 1) setPage(page - 1);
        setEffect(!effect);
      }
    }
  }, [pending]);

  const onDelete = (record: any) => {
    Modal.confirm({
      title: "Are you sure to delete this Track?",
      icon: <ExclamationCircleOutlined />,
      // content: "Some descriptions",
      okText: "Yes",
      // okType: "danger",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        await dispatch(deleteTrack({ id: record.id }));
        setMode(Actions.DELETE);
      },
      onCancel() {},
    });
  };

  const onEdit = (record: any) => {
    setMode(Actions.EDIT);
    setVisible(true);
    setEditData(record);
  };

  const handleFilters = () => {
    setSearchText("");
    setPage(1);
    setEffect(!effect);
    fetchLabelAndContributors();
  };

  const resetFilters = () => {
    filterForm.resetFields();
    setPage(1);
    setEffect(!effect);
    // for reseting the SelectFields component: isSelected and selectedValue
    setRefreshKey(Date.now().toString());
  };

  const actions = {
    title: "",
    key: "action",
    align: "right",
    width: 80,
    render: (_: any, record: any) =>
      isClapAdminORClapManager(userDetails) && (
        <div className="text-secondary">
          <Dropdown
            overlay={
              <Menu
                className="min-w-[100px] rounded-[3px]"
                items={[
                  {
                    label: (
                      <div onClick={() => onEdit(record)} aria-hidden="true">
                        Edit
                      </div>
                    ),
                    key: "EDIT",
                  },
                  {
                    label: (
                      <div onClick={() => onDelete(record)} aria-hidden="true">
                        Delete
                      </div>
                    ),
                    key: "DELETE",
                  },
                ]}
              />
            }
            trigger={["click"]}
          >
            <button type="button">{icons.DotsVerticle()}</button>
          </Dropdown>
        </div>
      ),
  };

  return (
    <div>
      <div>
        <CatalogHeader title="Tracks" handleClick={showModal} />
        <TrackModalForm
          visible={visible}
          onCancel={hideModal}
          callEffect={callEffect}
          mode={mode}
          editData={editData}
        />
      </div>
      <div className="mt-5 mb-4">
        <Form form={filterForm} layout="vertical" name="filterForm">
          <div className="default-border flex items-center gap-x-10 px-4 pt-4 pb-0 bg-lotion">
            <div className="grid grid-cols-6 w-full gap-x-[6px]">
              <div>
                <SelectFields
                  key={refreshkey}
                  name="Label"
                  options={[blankOB, ...allLabels]}
                />
              </div>
              <div>
                <SelectFields
                  key={refreshkey}
                  name="Artist"
                  options={[blankOB, ...allArtists]}
                />
              </div>
              <div>
                <SelectFields
                  key={refreshkey}
                  name="Lyricist"
                  options={[blankOB, ...allLyricists]}
                />
              </div>
              <div>
                <SelectFields
                  key={refreshkey}
                  name="Composer"
                  options={[blankOB, ...allComposers]}
                />
              </div>
              <div className="flex items-center -mb-2.5">
                <Button type="link" onClick={resetFilters}>
                  Reset
                </Button>
                <Button type="primary" onClick={handleFilters}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </Form>
        <div className="mt-4 mb-4 flex justify-between">
          <div>
            <SearchComponent handleSearch={handleSearch} />
          </div>
          {/* <div className="flex gap-x-1">
          <Button disabled>Batch Edit</Button>
          <Button>Export</Button>
          <Button
            icon={icons.DotsHorizontal()}
            className="flex items-center justify-center"
          />
        </div> */}
        </div>
      </div>
      <div>
        <Table
          // rowSelection={tracks.length && rowSelection}
          rowKey={(record: any) => {
            return record.id;
          }}
          columns={tableUtils.buildColumns(tracks, TrackColumn, actions)}
          dataSource={tracks}
          pagination={false}
          size="middle"
          scroll={{
            ...(globalUtils.checkIsScroll(tracks) && { y: 474 }),
            x: 1024,
          }}
          className="ant_table"
          loading={{
            spinning: tablePending as boolean,
            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
          }}
        />
      </div>
      <div className="text-right mt-4">
        <Pagination
          current={page}
          pageSize={PageSize.FIFTY}
          onChange={handlePageChange}
          total={tracksResponse?.count}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}

export default Tracks;
